import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { formatUsdCents } from '../../../lib/format'
import { trackEvent } from '../../../lib/gtag'
import { useRequestWithdrawalMutation } from '../hooks/use-request-withdrawal-mutation'

// "Request withdrawal" button + confirmation dialog for the outreach wallet.
// Lives on the Billing page's wallet card; disabled while the balance is zero
// (there's nothing to refund).
export function RequestWithdrawalButton({
  startupId,
  balanceCents,
}: {
  startupId: string
  balanceCents: number
}) {
  const withdrawal = useRequestWithdrawalMutation(startupId)
  const [open, setOpen] = useState(false)
  const canWithdraw = balanceCents > 0

  return (
    <>
      <SecondaryButton
        onClick={() => setOpen(true)}
        disabled={!canWithdraw}
        title={canWithdraw ? undefined : 'Nothing to refund, your balance is $0'}
      >
        Request withdrawal
      </SecondaryButton>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) withdrawal.reset()
        }}
      >
        <DialogContent className="sm:max-w-md">
          {withdrawal.isSuccess ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="size-5 text-emerald-400" />
                  Withdrawal requested
                </DialogTitle>
                <DialogDescription>
                  We've logged your request to refund {formatUsdCents(balanceCents)}. Our team
                  processes it within 1 business day.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setOpen(false)
                    withdrawal.reset()
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Request a withdrawal?</DialogTitle>
                <DialogDescription>
                  We'll refund your remaining wallet balance of {formatUsdCents(balanceCents)} to
                  your card. This usually takes up to 1 business day.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={withdrawal.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    withdrawal.mutate(undefined, {
                      onSuccess: () =>
                        trackEvent('outreach_withdrawal_requested', { startup_id: startupId }),
                    })
                  }
                  disabled={withdrawal.isPending}
                >
                  {withdrawal.isPending ? 'Requesting…' : 'Confirm withdrawal'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
